import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { X, Loader2, RefreshCw, CheckCircle2, AlertTriangle, Circle, Radio } from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
  cached:  { color: "bg-emerald-500/15 border-emerald-400/60 text-emerald-300", dot: "bg-emerald-400", icon: CheckCircle2, label: "Done" },
  failed:  { color: "bg-red-500/15 border-red-400/60 text-red-300",             dot: "bg-red-400",     icon: AlertTriangle, label: "Failed" },
  running: { color: "bg-amber-500/15 border-amber-400/70 text-amber-300",       dot: "bg-amber-400 animate-pulse", icon: Radio, label: "Running" },
  pending: { color: "bg-[#181A1F] border-[#2A2D35] text-[#6E6B64]",             dot: "bg-[#3A3D45]",   icon: Circle,       label: "Pending" },
};

const STEP_LABELS = {
  "chart-view":     "Chart Views",
  "alan-leo-phase": "Alan Leo Phases",
  "bphs-chapter":   "BPHS Classical",
  "scripture":      "Scripture",
  "atma-darshan":   "Atma Darshan",
  "homeopathy":     "Homeopathy",
  "full-life":      "Full Life (143)",
};

export default function MasterpieceScreener({ profileId, lang = "en", onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenBusy, setRegenBusy] = useState({});
  const [filter, setFilter] = useState("all");
  const [groupFilter, setGroupFilter] = useState("all");

  const load = useCallback(async () => {
    try {
      const d = await api.masterpieceScreener(profileId, lang);
      setData(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Screener load failed");
    } finally {
      setLoading(false);
    }
  }, [profileId, lang]);

  useEffect(() => { load(); }, [load]);

  // Auto-poll every 3 s while a batch is running so the tiles animate live
  useEffect(() => {
    if (!data) return;
    if (data.job_status !== "running") return;
    const id = setInterval(load, 3000);
    return () => clearInterval(id);
  }, [data, load]);

  const regenOne = async (row) => {
    const key = `${row.step}:${row.slug}`;
    setRegenBusy((m) => ({ ...m, [key]: true }));
    try {
      await api.masterpieceRegenerateItem(profileId, row.step, row.slug, lang);
      toast.success(`Regenerated: ${row.title}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Regeneration failed: ${row.title}`);
    } finally {
      setRegenBusy((m) => {
        const n = { ...m }; delete n[key]; return n;
      });
    }
  };

  const groupedRows = useMemo(() => {
    if (!data?.items) return [];
    let rows = data.items;
    if (filter !== "all") rows = rows.filter((r) => r.status === filter);
    if (groupFilter !== "all") rows = rows.filter((r) => r.step === groupFilter);
    return rows;
  }, [data, filter, groupFilter]);

  const groups = useMemo(() => {
    const g = {};
    (data?.items || []).forEach((r) => { g[r.step] = (g[r.step] || 0) + 1; });
    return g;
  }, [data]);

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-2 sm:p-4"
         onClick={onClose} data-testid="screener-overlay">
      <div onClick={(e) => e.stopPropagation()}
           className="w-full max-w-6xl max-h-[95vh] bg-[#0F1115] border border-[#D4AF37]/40 rounded-sm flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-5 border-b border-[#2A2D35]">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37] font-mono">Masterpiece Screener</div>
            <div className="font-heading text-lg sm:text-xl text-[#E6E1D6] truncate">All 205 readings — live status</div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button size="sm" variant="outline" onClick={load}
                    data-testid="screener-refresh"
                    className="rounded-sm border-[#2A2D35] text-[#A19E95] hover:text-[#D4AF37] hover:border-[#D4AF37]/40 h-8 px-3">
              <RefreshCw size={12} className="mr-1" /> Refresh
            </Button>
            <button onClick={onClose} data-testid="screener-close"
                    className="p-2 text-[#6E6B64] hover:text-[#E6E1D6]">
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Counts bar */}
        {data && (
          <div className="px-4 sm:px-5 py-3 border-b border-[#2A2D35] flex flex-wrap gap-2 items-center">
            {["all", "cached", "running", "failed", "pending"].map((k) => {
              const count = k === "all" ? data.total : (data.counts?.[k] || 0);
              const active = filter === k;
              return (
                <button key={k} onClick={() => setFilter(k)}
                        data-testid={`screener-filter-${k}`}
                        className={`text-[10px] uppercase tracking-widest font-mono px-3 py-1.5 rounded-sm border transition-colors
                          ${active ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10"
                                    : "border-[#2A2D35] text-[#A19E95] hover:border-[#D4AF37]/40"}`}>
                  {k} · {count}
                </button>
              );
            })}
            <div className="w-px h-6 bg-[#2A2D35] mx-1" />
            <select value={groupFilter} onChange={(e) => setGroupFilter(e.target.value)}
                    data-testid="screener-group-filter"
                    className="text-[10px] uppercase tracking-widest font-mono bg-[#181A1F] border border-[#2A2D35]
                               text-[#A19E95] px-3 py-1.5 rounded-sm">
              <option value="all">All Groups ({data.total})</option>
              {Object.entries(groups).map(([step, n]) => (
                <option key={step} value={step}>{STEP_LABELS[step] || step} · {n}</option>
              ))}
            </select>
            {data.job_status === "running" && (
              <div className="ml-auto flex items-center gap-2 text-amber-300 text-xs font-mono">
                <Loader2 size={12} className="animate-spin" />
                Batch running · current: {data.current || "…"}
              </div>
            )}
          </div>
        )}

        {/* Grid */}
        <div className="flex-1 overflow-auto p-3 sm:p-4">
          {loading && (
            <div className="flex items-center justify-center h-40 text-[#A19E95]">
              <Loader2 size={20} className="animate-spin mr-2" /> Loading…
            </div>
          )}
          {!loading && data && (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3">
              {groupedRows.map((row) => {
                const meta = STATUS_META[row.status] || STATUS_META.pending;
                const Icon = meta.icon;
                const key = `${row.step}:${row.slug}`;
                const busy = !!regenBusy[key];
                return (
                  <div key={key}
                       data-testid={`screener-tile-${row.step}-${row.slug}`}
                       data-status={row.status}
                       className={`border rounded-sm p-3 flex flex-col gap-2 min-h-[92px] ${meta.color}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="text-[9px] uppercase tracking-widest font-mono opacity-70">
                          {STEP_LABELS[row.step] || row.step}
                        </div>
                        <div className="text-xs font-medium leading-tight text-[#E6E1D6] truncate" title={row.title}>
                          {row.title}
                        </div>
                      </div>
                      <div className={`w-2.5 h-2.5 rounded-full mt-1 shrink-0 ${meta.dot}`} />
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1 text-[10px] font-mono">
                        <Icon size={10} /> {meta.label}
                      </div>
                      <button
                        onClick={() => regenOne(row)}
                        disabled={busy || row.status === "running"}
                        data-testid={`screener-regen-${row.step}-${row.slug}`}
                        title={row.error || "Regenerate this item"}
                        className="text-[9px] uppercase tracking-widest font-mono px-2 py-1 rounded-sm border
                                   border-current opacity-80 hover:opacity-100 disabled:opacity-40
                                   disabled:cursor-not-allowed flex items-center gap-1">
                        {busy ? <Loader2 size={9} className="animate-spin" /> : <RefreshCw size={9} />}
                        {busy ? "Working" : "Regen"}
                      </button>
                    </div>
                    {row.status === "failed" && row.error && (
                      <div className="text-[9px] font-mono opacity-70 line-clamp-2" title={row.error}>
                        {row.error}
                      </div>
                    )}
                  </div>
                );
              })}
              {groupedRows.length === 0 && (
                <div className="col-span-full text-center text-[#6E6B64] py-10 text-sm font-mono">
                  No items match this filter.
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer helper */}
        <div className="px-4 sm:px-5 py-3 border-t border-[#2A2D35] text-[10px] font-mono text-[#6E6B64]">
          Each reading auto-saves to Mongo the moment it's generated. Regen wipes just that one row and rebuilds it (~15–45 s).
        </div>
      </div>
    </div>
  );
}
